/* eslint-disable camelcase */
import React, { Suspense, lazy } from 'react';
import {
  useNavigate,
} from 'react-router';
import Dropzone from 'react-dropzone';
import {
  Formik,
  Field,
} from 'formik';

import {
  DocumentResource,
} from '@gymcloud/core';

import {
  Author,
  Button,
  Tooltip,
  TagList,
  TagField,
  TextField,
} from 'web-components';

import {
  Flex,
  Text,
  H100,
} from 'web-styled';

import {
  OpenedTooltip,
} from 'web-types';

import * as S from './styled';

const PdfViewer = lazy(() => import('./PdfViewer'));
const SpreadsheetPreview = lazy(() => import('./SpreadsheetPreview'));

type Props = {
  readOnly: boolean,
  author: {
    avatar: string,
    fullName: string,
  },
  document: DocumentResource,
  fileUpload: any,
  openedTooltips: OpenedTooltip[],
  onView: { (): void },
  onSubmit: { (): void },
  onDelete: { (): void },
  onDropFile: { (): void },
};

const Document = ({
  readOnly,
  author,
  document,
  fileUpload,
  openedTooltips,
  onView,
  onSubmit,
  onDelete,
  onDropFile,
}: Props) => {
  const navigate = useNavigate();
  const file = fileUpload[document.id];

  const Preview = {
    pdf: (
      <PdfViewer
        file={document.document_file.url}
      />
    ),
    xlsx: (
      <SpreadsheetPreview
        documentId={document.id}
        documentUrl={document.document_file.url}
      />
    ),
  }[document.filetype];

  return readOnly ? (
    <Flex
      column
      width="100%"
      spacing={10}
    >
      <S.Header>
        <Author
          avatar={author.avatar}
          fullName={author.fullName}
        />
        <Flex spacing={10}>
          <Button
            ghost
            appearance="primary"
            onClick={() => navigate(`/documents/${document.id}/edit`)}
          >
            Edit
          </Button>
          <Tooltip
            body="Click View to read or download the entire manual"
            toggleMode={openedTooltips.some(el => el.key === 'view_document')}
            initialState={openedTooltips.some(el => el.key === 'view_document')}
          >
            <Button
              ghost
              as="a"
              rel="noopener noreferrer"
              href={document.document_file?.url}
              target="_blank"
              appearance="primary"
              onClick={onView}
            >
              View
            </Button>
          </Tooltip>
          <Button
            ghost
            onClick={onDelete}
            appearance="danger"
          >
            Delete
          </Button>
        </Flex>
      </S.Header>
      <Flex
        width="100%"
        align="stretch"
        padding="15px 25px"
      >
        <Flex column spacing={5}>
          <Text color="#c1c1c1">Preview:</Text>
          <Suspense fallback={<div>Loading Viewer...</div>}>
            {Preview}
          </Suspense>
        </Flex>
        <Flex
          column
          width="calc(100% - 565px)"
          spacing={10}
          margin="15px 0 0 0"
          padding="0 0 30px 0"
        >
          <H100 color="primary">{document.name}</H100>
          <Text color="#c1c1c1">
            Click the Edit button to rename or replace your document
          </Text>
          <TagList
            tags={document.tag_list}
            resourceType="documents"
          />
        </Flex>
      </Flex>
    </Flex>
  ) : (
    <Formik
      enableReinitialize
      initialValues={{
        name: document.name,
        tag_list: document?.tag_list ? document.tag_list.join(',') : '',
      }}
      onSubmit={onSubmit}
    >
      {(formikProps) => (
        <Flex
          as="form"
          column
          width="100%"
          spacing={10}
          onSubmit={formikProps.handleSubmit}
        >
          <S.Header>
            <Author
              avatar={author.avatar}
              fullName={author.fullName}
            />
            <Flex spacing={10}>
              <Button
                ghost
                type="submit"
                appearance="primary"
                isLoading={formikProps.isSubmitting}
              >
                Save
              </Button>
              <Button
                ghost
                onClick={() => navigate(`/documents/${document.id}`)}
                appearance="danger"
              >
                Cancel
              </Button>
            </Flex>
          </S.Header>
          <Flex
            width="100%"
            align="stretch"
            padding="15px"
          >
            <Flex column spacing={5}>
              <Text color="#c1c1c1">Preview:</Text>
              {Preview}
            </Flex>
            <Flex
              column
              width="calc(100% - 565px)"
              spacing={10}
              margin="15px 0 0 0"
              padding="0 0 30px 0"
            >
              <Field
                name="name"
                label="Name"
                color="primary"
                autoComplete="off"
                placeholder="File"
                component={TextField}
              />

              <Flex
                column
                width="100%"
                spacing={10}
              >
                <Text
                  color="primary"
                >
                  Replace
                </Text>
                <Dropzone
                  accept="application/pdf"
                  multiple={false}
                  onDrop={onDropFile}
                >
                  {({ getRootProps, getInputProps, isDragActive }) => (
                    <S.StyledDropArea
                      {...getRootProps()}
                      isDragActive={isDragActive}
                    >
                      <input {...getInputProps()} />
                      <Text color="#808080">
                        Drop your
                        {' '}
                        <b>PDF</b>
                        {' '}
                        file here or
                      </Text>
                      <Button
                        ghost
                        appearance={isDragActive ? 'success' : 'primary'}
                      >
                        Upload From Computer
                      </Button>
                    </S.StyledDropArea>
                  )}
                </Dropzone>
              </Flex>

              {(file?.progress !== undefined) && (
                <Flex align="center" width="100%" spacing={8}>
                  <Text color="#808080">
                    {`${(file.size / 1000000).toFixed(2)}MB`}
                  </Text>
                  <S.Progress progress={file.progress} />
                  <Text color="#39ca74">{`${file.progress}%`}</Text>
                </Flex>
              )}

              <Field
                name="tag_list"
                component={TagField}
              />
            </Flex>
          </Flex>
        </Flex>
      )}
    </Formik>
  );
};

export default Document;
